import type { BreadcrumbItemType } from 'antd/es/breadcrumb/Breadcrumb';
import { AppBreadcrumb, AppButton, AppContainer, AppPagination, AppTabs } from '../atoms';
import { Eye, Refresh } from '../atoms/AppIcon';
import { NotificationCenterTable } from '../molecules';
import { NotificationCenterFilterPanel } from '../organisms';
import {
  DEFAULT_PER_PAGE,
  LEVEL_LABEL,
  NOTIFICATION_TAB,
  NOTIFICATION_TAB_HINT,
  NOTIFICATION_TAB_LABEL,
  hasActiveFilters,
  type NotificationFilters,
  type NotificationLevel,
  type NotificationItem,
  type NotificationListMeta,
  type NotificationTab,
  type NotificationUnreadCounts,
} from '@/types/admin-notification-center';

/**
 * The admin portal's three inboxes, and the default.
 *
 * The 治療院 portal passes its own two (レセプト同期 · 審査システムからの通知): a clinic does not send master
 * data, so a マスタ同期 tab there would list an action it cannot take, cannot retry and did not cause.
 */
const TABS: NotificationTab[] = [
  NOTIFICATION_TAB.MASTER,
  NOTIFICATION_TAB.REZEPT,
  NOTIFICATION_TAB.INBOUND,
];

/** Past 999 the exact number stops being information and starts being noise. */
const badgeText = (count: number): string => (count > 999 ? '999+' : String(count));

interface AdminNotificationCenterTemplateProps {
  breadcrumbItems: BreadcrumbItemType[];
  tab: NotificationTab;
  onTabChange: (tab: NotificationTab) => void;
  unreadCounts?: NotificationUnreadCounts;
  /** Which inboxes to offer. Defaults to the admin portal's three. */
  tabs?: NotificationTab[];
  /**
   * Draw and offer 治療院ID / 治療院名. FALSE on the 治療院 portal, where the reader is inside one clinic
   * and both would repeat the same value on every row. 👤 owner, 2026-09-02.
   */
  showClinicColumns?: boolean;
  /** Pass through to the table — 治療院 portal only. See NotificationCenterTableProps. */
  showAdminContactHint?: boolean;

  filters: NotificationFilters;
  onFiltersChange: (next: NotificationFilters) => void;

  data: NotificationItem[];
  meta?: NotificationListMeta;
  summary?: { info: number; success: number; error: number };
  isLoading?: boolean;
  onPageChange: (page: number) => void;

  selectedIds: number[];
  onSelectionChange: (ids: number[]) => void;

  onBulkRetry: () => void;
  onBulkMarkRead: () => void;
  onDownloadError: (item: NotificationItem) => void;
  /** 不一致明細 — a 状態の不一致 row's own file. Absent = the button is never drawn. */
  onDownloadDrift?: (item: NotificationItem) => void;
  /** Quick-filter chips on the 結果 summary strip. */
  /** Summary-chip shortcut — replaces the 種別 selection outright. */
  onLevelShortcut: (levels: NotificationLevel[]) => void;
  isBusy?: boolean;
}

/** The chips express a SINGLE 種別; a multi-value selection lights none of them rather than one at random. */
const only = (levels: NotificationLevel[]): NotificationLevel | null =>
  levels.length === 1 ? levels[0] : null;

const AdminNotificationCenterTemplate = ({
  breadcrumbItems,
  tab,
  onTabChange,
  unreadCounts,
  tabs = TABS,
  showClinicColumns = true,
  showAdminContactHint = false,
  filters,
  onFiltersChange,
  data,
  meta,
  summary,
  isLoading,
  onPageChange,
  selectedIds,
  onSelectionChange,
  onBulkRetry,
  onBulkMarkRead,
  onDownloadError,
  onDownloadDrift,
  onLevelShortcut,
  isBusy,
}: AdminNotificationCenterTemplateProps) => {
  const selectedCount = selectedIds.length;
  const nothingSelected = selectedCount === 0;
  const isInbound = tab === NOTIFICATION_TAB.INBOUND;

  // The selected rows, resolved against THIS page — the only rows the screen can name.
  const selectedOnPage = data.filter(item => selectedIds.includes(item.id));
  const retryableCount = selectedOnPage.filter(item => item.retryable).length;

  /**
   * 再送信 is ALL-OR-NOTHING over the selection. 👤 Owner's manager, 2026-08-27.
   *
   * It used to enable on 「at least one row can be re-sent」 and silently skip the rest, reporting the skip
   * afterwards. The manager's rule is stricter and better: if the ticks include a success, or a failure no
   * retry can fix, the button does not arm at all. A press then means exactly what it says — every ticked
   * row is going to be re-sent — instead of an action whose real scope you learn from the toast.
   *
   * The cost is a disabled button whose reason is not obvious, so the reason is written out below it.
   */
  const retryArmed = !nothingSelected && retryableCount === selectedCount;
  const mixedSelection = !nothingSelected && !retryArmed;

  return (
    <AppContainer>
      <main className="space-y-5 px-4 pb-8">
        <AppBreadcrumb items={breadcrumbItems} />

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="m-0 text-xl">通知センター</h3>
            <p className="m-0 mt-1 text-xs text-gray-500">
              同期の結果と、審査システムから届いた通知をまとめて確認できます。
            </p>
          </div>

          {/* The two bulk buttons.
              EVERY LABEL CARRIES THE COUNT once something is ticked — 「再送信（3件）」 — so the press is
              made against a number the operator can see, not against a selection they have to remember.
              Disabled while nothing is ticked rather than hidden: a control that vanishes teaches nobody
              that ticking a row is what turns it on.

              削除 IS GONE — 👤 owner's manager, 2026-08-27: 「không thấy ý nghĩa của nút xóa ở đây」. It only
              ever hid the row. The failed record stayed failed, so the button offered tidiness that looked
              like resolution, and an inbox that can be emptied stops being evidence of anything. */}
          <div className="flex flex-col items-end gap-1">
            <div className="flex flex-wrap items-center gap-2">
              <AppButton
                icon={<Eye />}
                disabled={nothingSelected || isBusy}
                onClick={onBulkMarkRead}
              >
                既読にする{nothingSelected ? '' : `（${selectedCount}件）`}
              </AppButton>
              {/* 再送信 — sending tabs only. Nothing on 審査システムからの通知 failed, so there is nothing
                  there to push again; the button would be permanently disabled. */}
              {!isInbound && (
                <AppButton
                  type="primary"
                  ghost
                  icon={<Refresh />}
                  disabled={!retryArmed || isBusy}
                  onClick={onBulkRetry}
                >
                  再送信{nothingSelected ? '' : `（${selectedCount}件）`}
                </AppButton>
              )}
            </div>
            {/* Says WHY the button is off. A disabled control with no reason is a support call, and the
                fix — untick the rows that cannot be re-sent — is not guessable from a grey button. */}
            {mixedSelection && !isInbound && (
              <span className="text-xs text-amber-700">
                再送信できない行が選択に含まれています（{selectedCount - retryableCount}件）。エラー行のみ選択してください。
              </span>
            )}
          </div>
        </div>

        <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <AppTabs
            className="app-inbox-tabs"
            activeKey={tab}
            onChange={value => onTabChange(value as NotificationTab)}
            items={tabs.map(value => ({
              key: value,
              label: (
                <span className="app-inbox-tabs__label">
                  {NOTIFICATION_TAB_LABEL[value]}
                  {/* Hidden at zero, so a clean inbox stays quiet instead of showing a 「0」 badge. */}
                  {(unreadCounts?.[value] ?? 0) > 0 && (
                    <span className="app-inbox-tabs__badge">
                      {badgeText(unreadCounts?.[value] ?? 0)}
                    </span>
                  )}
                </span>
              ),
            }))}
          />

          <p className="m-0 text-xs text-gray-500">{NOTIFICATION_TAB_HINT[tab]}</p>

          <NotificationCenterFilterPanel
            tab={tab}
            showClinicFilters={showClinicColumns}
            value={filters}
            onSearch={onFiltersChange}
            disabled={isBusy}
          />

          {/* 結果 summary strip.
              A total and three chips — エラー / 情報 / 成功. This is the screen's answer to 「is anything wrong
              right now?」, a question that otherwise costs a scroll through every page. The chips ARE the
              filter, so the number and the way to act on it sit in the same place.

              検索結果 COUNTS THE LIST; THE CHIPS COUNT WHAT THE LIST COULD HOLD. The server counts the
              breakdown before applying the 種別 filter, so the three numbers hold still while a chip is
              active and stop summing to 検索結果 — that is the point, not a rounding bug. Which of the three
              the list is showing is said by the active chip being drawn filled. */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm">
            <span className="text-gray-500">検索結果</span>
            <span className="font-semibold text-gray-800">{meta?.total ?? 0}件</span>
            {!isInbound && <span className="text-gray-300">|</span>}

            {/* エラー / 情報 / 成功 breakdown — sending tabs only, and the chips ARE the filter, so the
                number and the way to act on it sit in the same place. Pressing one REPLACES the 種別
                selection with just that value; pressing the active one clears it. The chips are a shortcut
                to the common single-value case, not a second way to build the multi-select. */}
            {!isInbound && (
              <>
                <button
                  type="button"
                  onClick={() => onLevelShortcut(only(filters.levels) === 'error' ? [] : ['error'])}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 transition ${
                    only(filters.levels) === 'error'
                      ? 'border-red-500 bg-red-500 text-white'
                      : 'border-red-200 bg-red-50 text-red-700 hover:border-red-400'
                  }`}
                >
                  <i className="fa fa-exclamation-circle" aria-hidden="true" />
                  {LEVEL_LABEL.error} {summary?.error ?? 0}件
                </button>

                {/* 情報 — ALWAYS RENDERED, zero included, like the other two. It used to appear only when
                    the count was above zero, on the reasoning that a chip reading 「0件」 says nothing; BD
                    §3.1 item 6 asks for the full breakdown instead, and it is the better answer here. A chip
                    that comes and goes takes the 情報 filter shortcut off the screen at exactly the moment
                    somebody wants to confirm there is nothing in flight, and it makes the strip change
                    width as rows settle. */}
                <button
                  type="button"
                  onClick={() => onLevelShortcut(only(filters.levels) === 'info' ? [] : ['info'])}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 transition ${
                    only(filters.levels) === 'info'
                      ? 'border-blue-500 bg-blue-500 text-white'
                      : 'border-blue-200 bg-blue-50 text-blue-700 hover:border-blue-400'
                  }`}
                >
                  <i className="fa fa-info-circle" aria-hidden="true" />
                  {LEVEL_LABEL.info} {summary?.info ?? 0}件
                </button>

                <button
                  type="button"
                  onClick={() => onLevelShortcut(only(filters.levels) === 'success' ? [] : ['success'])}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 transition ${
                    only(filters.levels) === 'success'
                      ? 'border-green-600 bg-green-600 text-white'
                      : 'border-green-200 bg-green-50 text-green-700 hover:border-green-400'
                  }`}
                >
                  <i className="fa fa-check-circle" aria-hidden="true" />
                  {LEVEL_LABEL.success} {summary?.success ?? 0}件
                </button>
              </>
            )}

            {/* Selection read-out lives on the same line as the counts, because 「3件を選択中」 has to be
                visible from wherever the eye is when the buttons at the top are pressed. */}
            {selectedCount > 0 && (
              <>
                <span className="text-gray-300">|</span>
                <span className="text-blue-700">{selectedCount}件を選択中</span>
                <button
                  type="button"
                  className="text-gray-500 underline hover:text-gray-700"
                  onClick={() => onSelectionChange([])}
                >
                  選択を解除
                </button>
              </>
            )}
          </div>

          <NotificationCenterTable
            tab={tab}
            showClinicColumns={showClinicColumns}
            showAdminContactHint={showAdminContactHint}
            data={data}
            isLoading={isLoading}
            // Decides WHICH empty sentence AppTable shows: 「該当する検索結果がありません」 when something
            // is narrowing the list, 「データがありません」 when the inbox is simply empty. The tab is not
            // a filter — switching inbox is choosing a mailbox, not searching inside one.
            hasFilters={hasActiveFilters(filters)}
            selectedIds={selectedIds}
            onSelectionChange={onSelectionChange}
            onDownloadError={onDownloadError}
            onDownloadDrift={onDownloadDrift}
            disabled={isBusy}
          />

          {/* Pager only — NO 表示件数 PICKER, on either portal.
              Owner, 2026-09-08: 「rule table common của hitowa không có phần 表示件数 … bỏ cả bên admin site」.

              The Hitowa common spec fixes every list in the product at 20 rows a page and says only 「hide the
              pager at 20 or fewer」 (.claude/skills/hitowa-common/SKILL.md § Pagination). AppPagination already
              carries showSizeChanger={false} and hideOnSinglePage for exactly that, so a size control here was
              this one screen disagreeing with every other list — and it was the reason `per_page` was on the
              URL at all.

              pageSize FOLLOWS THE SERVER (meta.perPage) rather than being hardcoded to 20: the pager's page
              count has to be computed from the size the response was actually built with, or the last page
              number is wrong the moment those two ever differ. */}
          <div className="flex justify-end">
            <AppPagination
              current={meta?.currentPage ?? 1}
              total={meta?.total ?? 0}
              pageSize={meta?.perPage ?? DEFAULT_PER_PAGE}
              onChange={onPageChange}
              className="!w-auto"
            />
          </div>
        </div>
      </main>
    </AppContainer>
  );
};

export default AdminNotificationCenterTemplate;
